/**
 * Pure, TZ-safe date/status helpers extracted from `CapacityManagerContext`.
 *
 * These were inline module-scoped functions inside a large context component,
 * which made them untestable. Extracting them here (following the
 * `issueHelper` precedent) lets the date-math and ISO-week logic — which is
 * genuinely bug-prone around timezones and year boundaries — be unit-tested.
 */

export type StatusCode = "P" | "R" | "";

/**
 * The capacity table's "no warehouse filter" value. It is a real entry in the
 * picker's option list (the toolbar renders it as "Select warehouse"), not an
 * empty string, so it is also the default the selection falls back to.
 */
export const ALL_WAREHOUSES = "All";

/**
 * The warehouse the picker should hold, given what the loaded rows actually
 * offer.
 *
 * The selection is persisted across navigation and reloads, so it can outlive
 * the data it was made against: a warehouse can stop appearing (the orders in
 * it closed, "Show one month only" narrowed the window) or the whole tenant can
 * change under it when a support user switches org. A `<select>` whose `value`
 * matches no `<option>` renders as the first option while the table filters on
 * the value that isn't there — i.e. an empty table with a picker that looks
 * like it is showing everything.
 *
 * `hasRows` is what keeps this from eating the selection on every load: until
 * the first fetch lands there are no rows, so no warehouse is "known" yet and
 * the stored value must be left exactly as it is.
 */
export function reconcileWarehouseSelection(
  selected: string,
  warehouses: string[],
  hasRows: boolean
): string {
  if (!hasRows) return selected;
  return warehouses.includes(selected) ? selected : ALL_WAREHOUSES;
}

/**
 * Parse the leading `YYYY-MM-DD` of a string into a **local** midnight Date
 * (avoids the UTC shift you'd get from `new Date("2026-01-01")`). Returns
 * `undefined` for empty/unparseable input.
 */
export function toLocalDateOnlyOrUndefined(s?: string): Date | undefined {
  if (!s) return undefined;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/** Format a Date as local `YYYY-MM-DD`, or `""` when absent. */
export function ymd(date?: Date): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Normalize a date-ish string to local `YYYY-MM-DD`, or `""` when unparseable. */
export function toYMDLocalOrBlank(s?: string): string {
  return ymd(toLocalDateOnlyOrUndefined(s));
}

/**
 * ISO-8601 week label (`"YYYY Week WW"`) for the given `YYYY-MM-DD` string.
 * Uses the ISO rule where the week belongs to the year containing its Thursday,
 * so late-December / early-January dates land in the correct ISO year.
 */
export function getISOWeekLabel(dateStr: string): string {
  const d = toLocalDateOnlyOrUndefined(dateStr)!;
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const year = tmp.getUTCFullYear();
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const weekNo = Math.ceil((((tmp.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  const ww = String(weekNo).padStart(2, "0");
  return `${year} Week ${ww}`;
}

/**
 * Remaining scheduled minutes = `scheduledMins - minsLogged`, clamped at 0 so
 * an over-logged job never shows negative remaining. Returns `null` unless both
 * inputs are real numbers (the grid renders blank for unknowns).
 */
export function computeMinsRemaining(
  scheduledMins: number | null,
  minsLogged: number | null
): number | null {
  return typeof scheduledMins === "number" && typeof minsLogged === "number"
    ? Math.max(0, scheduledMins - minsLogged)
    : null;
}

/** Map free-form status text to the canonical `"P"` / `"R"` / `""` codes. */
export function normalizeStatusToPROrBlank(s?: string): StatusCode {
  if (!s) return "";
  const t = s.trim().toUpperCase();
  if (t === "R" || t === "RELEASED") return "R";
  if (t === "P" || t === "PLANNED") return "P";
  return "";
}
